import helpers from "@/helpers/global";
import {
  hasAuthenticatedSession,
  isPublicAuthPath,
  normalizePath,
} from "@/utils/routeFlags";
import { safeGetItem } from "@/utils/storage";
import { getAIEndpointVuexStore } from "./aiEndpointStoreRef";
import {
  aiEndpointContextFromRootState,
  readStoredCountry,
} from "./getAIEndpoint";
import {
  getAIEndpointResolved,
  isAIRegionRoutingEnabled,
} from "./resolveAIEndpoint";
import { withSentryBrowser } from "./sentryClient";

/** Tag every route (including login/signup) so Sentry events are searchable by path. */
export function syncSentryRouteContext(path = ""): void {
  if (!process.env.VUE_APP_SENTRY_DSN) {
    return;
  }

  const routePath = normalizePath(path || window.location.pathname);
  withSentryBrowser((Sentry) => {
    Sentry.setTag("route_path", routePath);
    Sentry.setTag("is_public_auth", isPublicAuthPath(routePath) ? "yes" : "no");
  });
}

/** Set Sentry user from persisted session before Vuex user module finishes loading. */
export function syncSentrySessionFromStorage(): void {
  if (!process.env.VUE_APP_SENTRY_DSN) {
    return;
  }

  const userDataEncoded = safeGetItem("userData") || "";
  if (!userDataEncoded) {
    return;
  }

  let email = "";
  let displayName = "";
  try {
    const userData = JSON.parse(atob(userDataEncoded) || "{}");
    email = userData.email || "";
    displayName = userData.displayName || "";
  } catch {
    return;
  }

  if (!email) {
    return;
  }

  const emailKey = helpers.filterPath(helpers.filterEmail(email));

  withSentryBrowser((Sentry) => {
    Sentry.setUser({
      id: emailKey,
      email,
      username: displayName || undefined,
    });
    Sentry.setTag("session_source", "localStorage");
  });
}

/** Whether the current browser session should always be traced in Sentry. */
export function shouldAlwaysSampleSentrySession(): boolean {
  return hasAuthenticatedSession();
}

/** Mirror Firebase user/team country into Sentry for observability (not routing). */
export function syncSentryUserContext(): void {
  if (!process.env.VUE_APP_SENTRY_DSN) {
    return;
  }

  const store = getAIEndpointVuexStore();
  const rootState = store?.state;
  if (!rootState) {
    return;
  }

  const user = rootState.user?.user;
  const email = user?.email;

  withSentryBrowser((Sentry) => {
    if (!email) {
      clearSentryUserContextInternal(Sentry);
      return;
    }

    const userCountry = readStoredCountry(user);
    const teamCountry = readStoredCountry(rootState.team?.team);
    const endpoint = getAIEndpointResolved(
      aiEndpointContextFromRootState(rootState)
    );
    const emailKey = helpers.filterPath(helpers.filterEmail(email));

    Sentry.setUser({
      id: emailKey,
      email,
      username: user.displayName || undefined,
    });

    Sentry.setTag("user_country", userCountry ?? "unset");
    Sentry.setTag("team_country", teamCountry ?? "unset");
    Sentry.setTag("effective_country", endpoint.countryCode);
    Sentry.setTag("country_source", endpoint.source);
    Sentry.setTag(
      "ai_region_routing",
      isAIRegionRoutingEnabled() ? "on" : "off"
    );
  });
}

export function clearSentryUserContext(): void {
  if (!process.env.VUE_APP_SENTRY_DSN) {
    return;
  }

  withSentryBrowser((Sentry) => {
    clearSentryUserContextInternal(Sentry);
  });
}

function clearSentryUserContextInternal(
  Sentry: typeof import("@sentry/browser")
): void {
  Sentry.setUser(null);
  Sentry.setTag("user_country", "");
  Sentry.setTag("team_country", "");
  Sentry.setTag("effective_country", "");
  Sentry.setTag("country_source", "");
  Sentry.setTag("ai_region_routing", "");
}
